import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiErrorMessage } from '../../shared/api/errors';
import { useDebouncedValue } from '../../shared/lib/useDebouncedValue';
import { createClinic, listClinics, type Clinic } from './api';

export interface ClinicRef {
  id: string;
  name: string;
  city: string;
}

interface Props {
  initial: ClinicRef[];
  onChange: (ids: string[]) => void;
}

function label(c: ClinicRef | Clinic): string {
  return c.city ? `${c.name} · ${c.city}` : c.name;
}

/** Мультивыбор клиник врача: чипсы + поиск + создание на лету. */
export function ClinicMultiSelect({ initial, onChange }: Props) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<ClinicRef[]>(initial);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);
  const debounced = useDebouncedValue(query.trim(), 250);
  const initialKey = useMemo(() => initial.map((c) => c.id).sort().join(','), [initial]);

  useEffect(() => {
    setSelected(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const { data } = useQuery({
    queryKey: ['clinics', debounced],
    queryFn: () => listClinics({ search: debounced || undefined, limit: 20 }),
    enabled: open,
  });

  const commit = (next: ClinicRef[]) => {
    setSelected(next);
    onChange(next.map((c) => c.id));
  };

  const add = (c: Clinic) => {
    if (!selected.some((s) => s.id === c.id)) {
      commit([...selected, { id: c.id, name: c.name, city: c.city }]);
    }
    setQuery('');
    setOpen(false);
  };
  const remove = (id: string) => commit(selected.filter((c) => c.id !== id));

  const create = useMutation({
    mutationFn: () => createClinic({ name: query.trim() }),
    onSuccess: async (c) => {
      await queryClient.invalidateQueries({ queryKey: ['clinics'] });
      add(c);
    },
  });

  const options = (data?.items ?? []).filter((c) => !selected.some((s) => s.id === c.id));
  const exact = (data?.items ?? []).some((c) => c.name.toLowerCase() === query.trim().toLowerCase());
  const canCreate = query.trim().length >= 2 && !exact;

  return (
    <div ref={boxRef} className="relative">
      <div className="mt-1 flex flex-wrap gap-1.5 rounded-lg border border-line bg-surface p-2">
        {selected.map((c) => (
          <span
            key={c.id}
            className="inline-flex items-center gap-1 rounded-md bg-primary-050 px-2 py-0.5 text-xs font-medium text-primary"
          >
            {label(c)}
            <button type="button" onClick={() => remove(c.id)} className="text-primary/60 hover:text-danger">
              ✕
            </button>
          </span>
        ))}
        <input
          className="min-w-[8rem] flex-1 bg-transparent text-sm text-ink outline-none placeholder:text-muted/70"
          placeholder={selected.length ? '' : 'Добавить клинику…'}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
        />
      </div>

      {open && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-line bg-surface py-1 text-sm shadow-lg">
          {options.map((c) => (
            <button
              key={c.id}
              type="button"
              className="block w-full px-3 py-1.5 text-left text-ink hover:bg-canvas"
              onClick={() => add(c)}
            >
              {label(c)}
            </button>
          ))}
          {options.length === 0 && !canCreate && (
            <p className="px-3 py-1.5 text-muted">Начните вводить название…</p>
          )}
          {canCreate && (
            <button
              type="button"
              disabled={create.isPending}
              onClick={() => create.mutate()}
              className="block w-full px-3 py-1.5 text-left font-medium text-accent hover:bg-canvas disabled:opacity-50"
            >
              ＋ Создать клинику «{query.trim()}»
            </button>
          )}
          {create.isError && (
            <p className="px-3 py-1.5 text-xs text-danger">{apiErrorMessage(create.error)}</p>
          )}
        </div>
      )}
    </div>
  );
}
